import { useEffect, useState, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import api, { iamApi } from '@/api/client'
import type { SiteResponse, SiteConfigResponse, SiteThemeResponse, ThemeResponse, PaginatedResponse } from '@/api/types'
import {
  ArrowLeft, Save, Check, Eye, Copy, Shield, Globe, Landmark, CreditCard,
  Languages, FileText, Clock, Hash, ChevronUp, ChevronDown, Package,
  Settings, Palette, ToggleLeft, Search as SearchIcon, X,
} from 'lucide-react'
import { toast } from 'sonner'

// ─── Shared UI ───

function Field({ label, value, onChange, placeholder }: { label: string; value: string; onChange: (v: string) => void; placeholder?: string }) {
  return (
    <div>
      <label className="text-xs text-muted-foreground font-medium uppercase tracking-wider block mb-1">{label}</label>
      <input value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder}
        className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring" />
    </div>
  )
}

function SwitchToggle({ checked, onChange, size = 'md' }: { checked: boolean; onChange: (v: boolean) => void; size?: 'sm' | 'md' }) {
  const s = size === 'sm' ? 'h-4 w-7' : 'h-5 w-9'
  const dot = size === 'sm' ? 'h-3 w-3' : 'h-3.5 w-3.5'
  const translate = size === 'sm' ? 'translate-x-3' : 'translate-x-4'
  return (
    <button type="button" onClick={() => onChange(!checked)}
      className={`relative inline-flex ${s} items-center rounded-full transition-colors shrink-0 ${checked ? 'bg-accent' : 'bg-border'}`}>
      <span className={`inline-block ${dot} transform rounded-full bg-white transition-transform ${checked ? translate : 'translate-x-0.5'}`} />
    </button>
  )
}

function countryFlag(code: string): string {
  if (!code || code.length !== 2) return ''
  const codePoints = [...code.toUpperCase()].map(c => 0x1F1E6 + c.charCodeAt(0) - 65)
  return String.fromCodePoint(...codePoints)
}

function hslToHex(hsl: string): string {
  const match = hsl.match(/hsl\((\d+)\s+([\d.]+)%\s+([\d.]+)%\)/)
  if (!match) return '#cccccc'
  const h = Number(match[1]) / 360, s = Number(match[2]) / 100, l = Number(match[3]) / 100
  const hue2rgb = (p: number, q: number, t: number) => { if (t < 0) t += 1; if (t > 1) t -= 1; if (t < 1/6) return p + (q - p) * 6 * t; if (t < 1/2) return q; if (t < 2/3) return p + (q - p) * (2/3 - t) * 6; return p }
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s, p = 2 * l - q
  const r = Math.round(hue2rgb(p, q, h + 1/3) * 255), g = Math.round(hue2rgb(p, q, h) * 255), b = Math.round(hue2rgb(p, q, h - 1/3) * 255)
  return '#' + [r, g, b].map(x => x.toString(16).padStart(2, '0')).join('')
}

function hexToHsl(hex: string): string {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
  if (!result) return hex
  const r = parseInt(result[1], 16) / 255, g = parseInt(result[2], 16) / 255, b = parseInt(result[3], 16) / 255
  const max = Math.max(r, g, b), min = Math.min(r, g, b), l = (max + min) / 2
  if (max === min) return `hsl(0 0% ${Math.round(l * 100)}%)`
  const d = max - min, s = l > 0.5 ? d / (2 - max - min) : d / (max + min)
  let h = 0
  if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6
  else if (max === g) h = ((b - r) / d + 2) / 6
  else h = ((r - g) / d + 4) / 6
  return `hsl(${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%)`
}

function ColorField({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) {
  return (
    <div className="flex items-center gap-2">
      <input type="color" value={hslToHex(value)} onChange={e => onChange(hexToHsl(e.target.value))}
        className="w-6 h-6 rounded border border-border shrink-0 cursor-pointer p-0 bg-transparent [&::-webkit-color-swatch-wrapper]:p-0 [&::-webkit-color-swatch]:rounded [&::-webkit-color-swatch]:border-0" />
      <input value={value} onChange={e => onChange(e.target.value)}
        className="flex-1 rounded-lg border border-input bg-background px-2 py-1 text-xs font-mono focus:outline-none focus:ring-1 focus:ring-ring" />
      <span className="text-[10px] text-muted-foreground/50 w-28 truncate">{label}</span>
    </div>
  )
}

function SectionCard({ title, children, icon: Icon }: { title: string; children: React.ReactNode; icon?: typeof Globe }) {
  return (
    <div className="bg-card border border-border rounded-xl overflow-hidden">
      <div className="px-5 py-3 border-b border-border bg-muted/20 flex items-center gap-2">
        {Icon && <Icon className="h-3.5 w-3.5 text-accent" />}
        <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">{title}</h3>
      </div>
      <div className="p-5">{children}</div>
    </div>
  )
}

// ─── Config helpers ───

function getConfigValue(configs: SiteConfigResponse[], key: string): string {
  const c = configs.find(cfg => cfg.key === key)
  if (!c) return ''
  try { return JSON.parse(c.value) } catch { return c.value }
}

function getConfigJSON(configs: SiteConfigResponse[], key: string): Record<string, unknown> {
  const c = configs.find(cfg => cfg.key === key)
  if (!c) return {}
  try { return JSON.parse(c.value) } catch { return {} }
}

// ─── Types ───

interface InheritedItem {
  id: string; name: string; disabled: boolean; sort_order: number | null
  inherited?: boolean; extra?: Record<string, unknown>
}

interface JurisdictionInfo {
  site_id: number; jurisdiction_id: number | null
  jurisdiction?: Record<string, unknown>; country_count?: number
}

// ─── Navigation config ───

const NAV_SECTIONS = [
  {
    label: '',
    items: [
      { key: 'general', label: 'Overview', icon: Settings },
    ],
  },
  {
    label: 'Platform',
    items: [
      { key: 'jurisdiction', label: 'Jurisdiction', icon: Shield },
      { key: 'products', label: 'Products', icon: Package },
    ],
  },
  {
    label: 'Inherited Catalogs',
    items: [
      { key: 'countries', label: 'Countries', icon: Globe },
      { key: 'banks', label: 'Banks', icon: Landmark },
      { key: 'payment_methods', label: 'Payment Methods', icon: CreditCard },
      { key: 'languages', label: 'Languages', icon: Languages },
      { key: 'currencies', label: 'Currencies', icon: Globe },
      { key: 'document_types', label: 'Documents', icon: FileText },
      { key: 'timezones', label: 'Timezones', icon: Clock },
      { key: 'bank_account_formats', label: 'Account Formats', icon: Hash },
    ],
  },
]

const CATALOG_KEYS = new Set(['countries', 'banks', 'payment_methods', 'languages', 'currencies', 'document_types', 'timezones', 'bank_account_formats'])
const CONFIG_KEYS = new Set(['general'])

// Product config sub-tabs
const PRODUCT_TABS = [
  { key: 'p_branding', label: 'Branding', icon: Globe },
  { key: 'p_theme', label: 'Theme', icon: Palette },
  { key: 'p_features', label: 'Features', icon: ToggleLeft },
  { key: 'p_preview', label: 'JSON Preview', icon: Eye },
]

// ─── Main component ───

export function SiteDetailPage() {
  const { t } = useTranslation()
  const { id, '*': splat } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const tenantId = (location.state as { tenantId?: number })?.tenantId

  // Derive activeTab from URL path
  const activeTab = useMemo(() => {
    if (!splat || splat === '') return 'general'
    // /sites/:id/products/:pid/branding → products/123/branding
    const parts = splat.split('/')
    if (parts[0] === 'products' && parts.length >= 3) return `p_${parts[2]}`
    if (parts[0] === 'products' && parts.length === 1) return 'products'
    return parts[0] || 'general'
  }, [splat])

  // Extract productId from URL for product tabs
  const urlProductId = useMemo(() => {
    if (!splat) return null
    const parts = splat.split('/')
    if (parts[0] === 'products' && parts[1]) return Number(parts[1])
    return null
  }, [splat])

  const setActiveTab = useCallback((tab: string, productId?: number) => {
    const base = `/sites/${id}`
    if (tab === 'general') { navigate(base, { replace: true }); return }
    if (tab === 'products') { navigate(`${base}/products`, { replace: true }); return }
    if (tab.startsWith('p_') && productId) {
      navigate(`${base}/products/${productId}/${tab.slice(2)}`, { replace: true }); return
    }
    navigate(`${base}/${tab}`, { replace: true })
  }, [id, navigate])

  const [site, setSite] = useState<SiteResponse | null>(null)
  const [siteTheme, setSiteTheme] = useState<SiteThemeResponse | null>(null)
  const [themes, setThemes] = useState<ThemeResponse[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const [jurisdictionInfo, setJurisdictionInfo] = useState<JurisdictionInfo | null>(null)
  const [jurisdictions, setJurisdictions] = useState<Record<string, unknown>[]>([])
  const [, setConfigs] = useState<SiteConfigResponse[]>([])
  const [branding, setBranding] = useState<Record<string, string>>({})
  const [themeConfig, setThemeConfig] = useState<Record<string, unknown>>({})
  const [features, setFeatures] = useState<Record<string, boolean>>({})
  const [previewJSON, setPreviewJSON] = useState<string | null>(null)
  const [inheritedItems, setInheritedItems] = useState<InheritedItem[]>([])
  const [inheritedLoading, setInheritedLoading] = useState(false)
  const [inheritedSaving, setInheritedSaving] = useState(false)
  const [catalogSearch, setCatalogSearch] = useState('')
  const [showAddPanel, setShowAddPanel] = useState(false)
  const [availableItems, setAvailableItems] = useState<InheritedItem[]>([])
  const [availableSearch, setAvailableSearch] = useState('')
  const [availableLoading, setAvailableLoading] = useState(false)
  const [products, setProducts] = useState<Record<string, unknown>[]>([])
  const [siteProducts, setSiteProducts] = useState<{ product_id: number; enabled: boolean }[]>([])
  const [productConfirm, setProductConfirm] = useState<{ prodId: number; name: string; enabling: boolean } | null>(null)
  const [selectedProduct, setSelectedProduct] = useState<{ id: number; name: string; code: string } | null>(null)
  const [productConfigs, setProductConfigs] = useState<SiteConfigResponse[]>([])
  const [themeTab, setThemeTab] = useState('colors')

  const loadConfigs = useCallback((cfgs: SiteConfigResponse[]) => {
    setConfigs(cfgs)
  }, [])

  const loadProductConfigs = useCallback((cfgs: SiteConfigResponse[]) => {
    setProductConfigs(cfgs)
    setBranding(getConfigJSON(cfgs, 'branding') as Record<string, string>)
    setFeatures(getConfigJSON(cfgs, 'features') as Record<string, boolean>)
    const th = getConfigJSON(cfgs, 'theme')
    // If no theme config saved yet, load from active theme defaults
    if (Object.keys(th).length === 0 && siteTheme) {
      const activeTheme = themes.find(t => t.id === siteTheme.theme_id)
      if (activeTheme) {
        try { setThemeConfig(JSON.parse(activeTheme.default_config)) } catch { setThemeConfig({}) }
      }
    } else {
      setThemeConfig(th)
    }
  }, [siteTheme, themes])

  useEffect(() => {
    if (!id) return
    setLoading(true)
    const fetchSite = async (): Promise<SiteResponse | null> => {
      if (tenantId) return (await iamApi.get<SiteResponse>(`/tenants/${tenantId}/sites/${id}`)).data
      const { data } = await iamApi.get<PaginatedResponse<SiteResponse>>('/sites', { params: { limit: 250 } })
      return (data.data || []).find(s => s.id === Number(id)) || null
    }
    Promise.all([
      fetchSite().catch(() => null),
      api.get<SiteConfigResponse[]>(`/sites/${id}/config`).catch(() => ({ data: [] as SiteConfigResponse[] })),
      api.get<SiteThemeResponse>(`/sites/${id}/theme`).catch(() => ({ data: null })),
      api.get<ThemeResponse[]>('/themes').catch(() => ({ data: [] as ThemeResponse[] })),
      api.get<JurisdictionInfo>(`/sites/${id}/jurisdiction`).catch(() => ({ data: null })),
      api.get<{ data: Record<string, unknown>[] }>('/catalog/gaming_jurisdictions?limit=100').catch(() => ({ data: { data: [] } })),
      api.get<{ data: Record<string, unknown>[] }>('/catalog/products?limit=100').catch(() => ({ data: { data: [] } })),
      api.get<{ product_id: number; enabled: boolean }[]>(`/sites/${id}/products`).catch(() => ({ data: [] })),
    ]).then(([siteData, configRes, themeRes, themesRes, jurRes, jurListRes, productsRes, siteProdsRes]) => {
      setSite(siteData)
      loadConfigs(configRes.data || [])
      setSiteTheme(themeRes.data)
      setThemes(themesRes.data || [])
      setJurisdictionInfo(jurRes.data)
      setJurisdictions((jurListRes.data as unknown as { data: Record<string, unknown>[] })?.data || [])
      setProducts((productsRes.data as unknown as { data: Record<string, unknown>[] })?.data || [])
      setSiteProducts(siteProdsRes.data || [])

      // If URL has a product path, auto-select that product
      if (urlProductId) {
        const allProds = (productsRes.data as unknown as { data: Record<string, unknown>[] })?.data || []
        const prod = allProds.find(p => Number(p.id) === urlProductId)
        if (prod) {
          setSelectedProduct({ id: urlProductId, name: String(prod.name), code: String(prod.code) })
          api.get<SiteConfigResponse[]>(`/sites/${id}/products/${urlProductId}/config`)
            .then(({ data: cfgs }) => loadProductConfigs(cfgs || []))
            .catch(() => loadProductConfigs([]))
        }
      }
    }).catch(() => {}).finally(() => setLoading(false))
  }, [id, tenantId, loadConfigs, urlProductId])

  useEffect(() => {
    if (!id || !CATALOG_KEYS.has(activeTab)) return
    setInheritedLoading(true)
    setCatalogSearch('')
    setShowAddPanel(false)
    api.get<{ items: InheritedItem[] }>(`/sites/${id}/inherited/${activeTab}`)
      .then(({ data }) => setInheritedItems(data.items || []))
      .catch(() => setInheritedItems([]))
      .finally(() => setInheritedLoading(false))
  }, [id, activeTab])

  const filteredItems = useMemo(() => {
    if (!catalogSearch) return inheritedItems
    const q = catalogSearch.toLowerCase()
    return inheritedItems.filter(item =>
      item.name.toLowerCase().includes(q) || item.id.toLowerCase().includes(q)
    )
  }, [inheritedItems, catalogSearch])

  const saveConfigs = async () => {
    if (!id) return
    setSaving(true); setSaved(false)
    try {
      await api.put(`/sites/${id}/config`, [])
      setSaved(true); setTimeout(() => setSaved(false), 2000)
      toast.success('Configuration saved')
    } catch { toast.error('Failed to save configuration') }
    setSaving(false)
  }

  const saveOverrides = async () => {
    if (!id) return
    setInheritedSaving(true)
    try {
      const overrides = inheritedItems
        .filter(item => item.disabled || item.sort_order !== null)
        .map(item => ({ item_id: item.id, disabled: item.disabled, sort_order: item.sort_order }))
      await api.put(`/sites/${id}/overrides/${activeTab}`, overrides)
      toast.success('Overrides saved')
    } catch { toast.error('Failed to save overrides') }
    setInheritedSaving(false)
  }

  const toggleItem = (itemId: string) => {
    setInheritedItems(prev => prev.map(item => item.id === itemId ? { ...item, disabled: !item.disabled } : item))
  }

  const moveItem = (idx: number, direction: 'up' | 'down') => {
    const realIdx = inheritedItems.indexOf(filteredItems[idx])
    if (realIdx === -1) return
    setInheritedItems(prev => {
      const items = [...prev]
      const swapIdx = direction === 'up' ? realIdx - 1 : realIdx + 1
      if (swapIdx < 0 || swapIdx >= items.length) return prev
      ;[items[realIdx], items[swapIdx]] = [items[swapIdx], items[realIdx]]
      return items.map((item, i) => ({ ...item, sort_order: i + 1 }))
    })
  }

  const loadAvailable = async (search?: string) => {
    if (!id) return
    setAvailableLoading(true)
    try {
      const params: Record<string, string> = {}
      if (search) params.search = search
      const { data } = await api.get<{ items: InheritedItem[] }>(`/sites/${id}/available/${activeTab}`, { params })
      setAvailableItems(data.items || [])
    } catch { setAvailableItems([]) }
    setAvailableLoading(false)
  }

  const addCustomItem = async (item: InheritedItem) => {
    if (!id) return
    // Add override for this item (not disabled)
    try {
      await api.put(`/sites/${id}/overrides/${activeTab}`, [{ item_id: item.id, disabled: false, sort_order: null }])
      const { data } = await api.get<{ items: InheritedItem[] }>(`/sites/${id}/inherited/${activeTab}`)
      setInheritedItems(data.items || [])
      setAvailableItems(prev => prev.filter(i => i.id !== item.id))
      toast.success(`${item.name} added`)
    } catch { toast.error('Failed to add item') }
  }

  const removeCustomItem = async (itemId: string) => {
    if (!id) return
    // Delete the override for this custom item
    try {
      await api.put(`/sites/${id}/overrides/${activeTab}`, [{ item_id: itemId, disabled: true, sort_order: null }])
      const { data } = await api.get<{ items: InheritedItem[] }>(`/sites/${id}/inherited/${activeTab}`)
      setInheritedItems(data.items || [])
    } catch {}
  }

  const enterProductConfig = async (prod: Record<string, unknown>) => {
    if (!id) return
    const prodId = Number(prod.id)
    setSelectedProduct({ id: prodId, name: String(prod.name), code: String(prod.code) })
    setActiveTab('p_branding', prodId)
    try {
      const { data } = await api.get<SiteConfigResponse[]>(`/sites/${id}/products/${prodId}/config`)
      loadProductConfigs(data || [])
    } catch { loadProductConfigs([]) }
  }

  const exitProductConfig = () => {
    setSelectedProduct(null)
    setActiveTab('products')
  }

  const saveProductConfig = async () => {
    if (!id || !selectedProduct) return
    setSaving(true); setSaved(false)
    try {
      await api.put(`/sites/${id}/products/${selectedProduct.id}/config`, [
        { key: 'branding', value: JSON.stringify(branding), type: 'json' },
        { key: 'features', value: JSON.stringify(features), type: 'json' },
        { key: 'theme', value: JSON.stringify(themeConfig), type: 'json' },
      ])
      setSaved(true); setTimeout(() => setSaved(false), 2000)
      toast.success('Product configuration saved')
    } catch { toast.error('Failed to save product configuration') }
    setSaving(false)
  }

  const confirmProductToggle = async () => {
    if (!id || !productConfirm) return
    const { prodId, enabling } = productConfirm
    const updated = siteProducts.some(p => p.product_id === prodId)
      ? siteProducts.map(p => p.product_id === prodId ? { ...p, enabled: enabling } : p)
      : [...siteProducts, { product_id: prodId, enabled: enabling }]
    setSiteProducts(updated)
    setProductConfirm(null)
    try {
      await api.put(`/sites/${id}/products`, updated)
      toast.success(`${productConfirm.name} ${enabling ? 'activated' : 'deactivated'}`)
    } catch { toast.error('Failed to update product') }
  }

  const assignJurisdiction = async (jurisdictionId: number) => {
    if (!id) return
    try {
      await api.put(`/sites/${id}/jurisdiction`, { jurisdiction_id: jurisdictionId })
      const { data } = await api.get<JurisdictionInfo>(`/sites/${id}/jurisdiction`)
      setJurisdictionInfo(data)
      toast.success('Jurisdiction updated')
    } catch { toast.error('Failed to update jurisdiction') }
  }

  if (loading) return <p className="text-muted-foreground p-6">{t('common.loading')}</p>
  if (!site) return <p className="text-destructive p-6">{t('common.error')}</p>

  const isCatalogTab = CATALOG_KEYS.has(activeTab)
  const isConfigTab = CONFIG_KEYS.has(activeTab)
  const isProductTab = activeTab.startsWith('p_')
  const enabledCount = inheritedItems.filter(i => !i.disabled).length

  const statusColors: Record<string, string> = {
    onboarding: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
    active: 'bg-green-500/20 text-green-400 border-green-500/30',
    draft: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
    maintenance: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
    disabled: 'bg-red-500/20 text-red-400 border-red-500/30',
  }

  const statusLabels: Record<string, string> = {
    onboarding: 'Onboarding',
    active: 'Live',
    draft: 'Draft',
    maintenance: 'Maintenance',
    disabled: 'Disabled',
  }

  return (
    <div className="flex gap-6 min-h-[calc(100vh-8rem)]">
      {/* ─── Left sidebar navigation ─── */}
      <div className="w-52 shrink-0">
        <button onClick={() => navigate('/sites')} className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors mb-4 text-sm">
          <ArrowLeft className="h-4 w-4" /> Back to sites
        </button>

        <div className="mb-4">
          <h2 className="text-lg font-bold text-foreground leading-tight">{site.name}</h2>
          <div className="flex items-center gap-2 mt-1">
            <span className={`text-[10px] px-1.5 py-0.5 rounded border font-medium ${statusColors[site.status] || statusColors.disabled}`}>
              {statusLabels[site.status] || site.status}
            </span>
            {jurisdictionInfo?.jurisdiction && (
              <span className="text-[10px] text-muted-foreground truncate">
                {String(jurisdictionInfo.jurisdiction.code || '')}
              </span>
            )}
          </div>
          {site.domain && <p className="text-[10px] text-muted-foreground mt-1 truncate">{site.domain}</p>}
        </div>

        <nav className="space-y-4">
          {selectedProduct ? (
            <>
              <div>
                <button onClick={exitProductConfig}
                  className="w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-muted/30 mb-2">
                  <ArrowLeft className="h-3.5 w-3.5 shrink-0" />
                  <span className="truncate">Back to site</span>
                </button>
                <p className="text-[10px] text-muted-foreground/60 uppercase tracking-wider font-semibold mb-1 px-2">
                  {selectedProduct.name}
                </p>
                <p className="text-[10px] text-muted-foreground/40 font-mono mb-2 px-2">{selectedProduct.code}</p>
              </div>
              <div>
                <p className="text-[10px] text-muted-foreground/60 uppercase tracking-wider font-semibold mb-1 px-2">Product Config</p>
                {PRODUCT_TABS.map((item) => {
                  const Icon = item.icon
                  const isActive = activeTab === item.key
                  return (
                    <button key={item.key} onClick={() => { setActiveTab(item.key, selectedProduct.id); if (item.key === 'p_preview') { api.get(`/sites/${id}/products/${selectedProduct.id}/config`).then(({ data }) => setPreviewJSON(JSON.stringify(data, null, 2))).catch(() => {}) } }}
                      className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                        isActive ? 'bg-accent/10 text-accent' : 'text-muted-foreground hover:text-foreground hover:bg-muted/30'
                      }`}>
                      <Icon className="h-3.5 w-3.5 shrink-0" />
                      <span className="truncate">{item.label}</span>
                    </button>
                  )
                })}
              </div>
            </>
          ) : (
            NAV_SECTIONS.map((section) => (
              <div key={section.label}>
                {section.label && <p className="text-[10px] text-muted-foreground/60 uppercase tracking-wider font-semibold mb-1 px-2">{section.label}</p>}
                {section.items.map((item) => {
                  const Icon = item.icon
                  const isActive = activeTab === item.key
                  return (
                    <button key={item.key} onClick={() => { setActiveTab(item.key); if (item.key === 'preview') { api.get(`/sites/${id}/configuration`).then(({ data }) => setPreviewJSON(JSON.stringify(data, null, 2))).catch(() => {}) } }}
                      className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                        isActive ? 'bg-accent/10 text-accent' : 'text-muted-foreground hover:text-foreground hover:bg-muted/30'
                      }`}>
                      <Icon className="h-3.5 w-3.5 shrink-0" />
                      <span className="truncate">{item.label}</span>
                    </button>
                  )
                })}
              </div>
            ))
          )}
        </nav>
      </div>

      {/* ─── Content area ─── */}
      <div className="flex-1 min-w-0">
        {/* Action bar */}
        <div className="flex items-center justify-between mb-5">
          <div>
            <h1 className="text-lg font-semibold text-foreground">
              {selectedProduct
                ? PRODUCT_TABS.find(t => t.key === activeTab)?.label || selectedProduct.name
                : NAV_SECTIONS.flatMap(s => s.items).find(i => i.key === activeTab)?.label || activeTab}
            </h1>
            {selectedProduct && (
              <p className="text-xs text-muted-foreground">{selectedProduct.name} ({selectedProduct.code})</p>
            )}
          </div>
          <div className="flex items-center gap-2">
            {isCatalogTab && (
              <button onClick={saveOverrides} disabled={inheritedSaving}
                className="flex items-center gap-2 px-3 py-1.5 text-xs rounded-lg bg-accent text-accent-foreground hover:bg-accent/90 transition-colors disabled:opacity-50 font-medium">
                <Save className="h-3.5 w-3.5" /> {inheritedSaving ? 'Saving...' : 'Save'}
              </button>
            )}
            {(isProductTab && activeTab !== 'p_preview') && (
              <button onClick={saveProductConfig} disabled={saving}
                className="flex items-center gap-2 px-3 py-1.5 text-xs rounded-lg bg-accent text-accent-foreground hover:bg-accent/90 transition-colors disabled:opacity-50 font-medium">
                {saved ? <Check className="h-3.5 w-3.5" /> : <Save className="h-3.5 w-3.5" />}
                {saving ? 'Saving...' : saved ? 'Saved!' : 'Save'}
              </button>
            )}
          </div>
        </div>

        {/* ─── JURISDICTION ─── */}
        {activeTab === 'jurisdiction' && (
          <div className="space-y-4 max-w-xl">
            <SectionCard title="Gaming Jurisdiction" icon={Shield}>
              <p className="text-xs text-muted-foreground mb-3">
                All catalog items will be inherited from the jurisdiction's associated countries.
              </p>
              <select value={jurisdictionInfo?.jurisdiction_id || ''}
                onChange={e => { const jid = Number(e.target.value); if (jid) assignJurisdiction(jid) }}
                className="w-full rounded-lg border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring">
                <option value="">— Select jurisdiction —</option>
                {jurisdictions.map((j) => (
                  <option key={String(j.id)} value={String(j.id)}>{String(j.name)} ({String(j.code)})</option>
                ))}
              </select>
              {jurisdictionInfo?.jurisdiction && (
                <div className="mt-3 bg-accent/5 border border-accent/20 rounded-lg p-3 flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-foreground">{String(jurisdictionInfo.jurisdiction.name)}</p>
                    <p className="text-xs text-muted-foreground">{jurisdictionInfo.country_count || 0} countries</p>
                  </div>
                  {jurisdictionInfo.jurisdiction.website && (
                    <a href={String(jurisdictionInfo.jurisdiction.website)} target="_blank" rel="noopener noreferrer"
                      className="text-xs text-accent hover:underline">{String(jurisdictionInfo.jurisdiction.website)}</a>
                  )}
                </div>
              )}
            </SectionCard>
          </div>
        )}

        {/* ─── CATALOG INHERITED ─── */}
        {isCatalogTab && (
          <div>
            {!jurisdictionInfo?.jurisdiction_id ? (
              <div className="bg-card border border-border rounded-xl p-12 text-center">
                <Shield className="h-10 w-10 text-muted-foreground/30 mx-auto mb-3" />
                <p className="text-sm text-muted-foreground">Assign a jurisdiction first.</p>
              </div>
            ) : inheritedLoading ? (
              <div className="bg-card border border-border rounded-xl p-12 text-center">
                <p className="text-sm text-muted-foreground">Loading...</p>
              </div>
            ) : (
              <>
              <div className="bg-card border border-border rounded-xl overflow-hidden">
                {/* Toolbar */}
                <div className="px-4 py-2.5 border-b border-border bg-muted/20 flex items-center gap-3">
                  <div className="relative flex-1 max-w-xs">
                    <SearchIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                    <input value={catalogSearch} onChange={e => setCatalogSearch(e.target.value)}
                      placeholder="Filter..."
                      className="w-full pl-8 pr-7 py-1.5 rounded-lg border border-border bg-background text-xs focus:outline-none focus:ring-1 focus:ring-ring" />
                    {catalogSearch && (
                      <button onClick={() => setCatalogSearch('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                        <X className="h-3 w-3" />
                      </button>
                    )}
                  </div>
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <span>{inheritedItems.length} total</span>
                    <span className="text-accent font-medium">{enabledCount} enabled</span>
                    {inheritedItems.length - enabledCount > 0 && (
                      <span className="text-red-400">{inheritedItems.length - enabledCount} disabled</span>
                    )}
                    <span className="text-border">|</span>
                    <button onClick={() => setInheritedItems(prev => prev.map(i => ({ ...i, disabled: false })))}
                      className="hover:text-accent transition-colors">Select all</button>
                    <button onClick={() => setInheritedItems(prev => prev.map(i => ({ ...i, disabled: true })))}
                      className="hover:text-red-400 transition-colors">Deselect all</button>
                    <button onClick={() => setInheritedItems(prev => {
                      const sorted = [...prev].sort((a, b) => a.name.localeCompare(b.name))
                      return sorted.map((item, i) => ({ ...item, sort_order: i + 1 }))
                    })} className="hover:text-accent transition-colors">A→Z</button>
                    <span className="text-border">|</span>
                    <button onClick={() => { setShowAddPanel(!showAddPanel); if (!showAddPanel) loadAvailable() }}
                      className="hover:text-accent transition-colors font-medium">
                      {showAddPanel ? 'Close' : '+ Add'}
                    </button>
                  </div>
                </div>

                {/* Items */}
                <div className="divide-y divide-border/20 max-h-[calc(100vh-16rem)] overflow-y-auto">
                  {filteredItems.map((item, idx) => (
                    <div key={item.id}
                      className={`flex items-center gap-2 px-4 py-2 transition-colors group ${item.disabled ? 'opacity-35 bg-muted/10' : 'hover:bg-muted/10'}`}>
                      {/* Reorder */}
                      {!catalogSearch && (
                        <div className="flex flex-col opacity-0 group-hover:opacity-100 transition-opacity">
                          <button onClick={() => moveItem(idx, 'up')} disabled={idx === 0}
                            className="p-0.5 text-muted-foreground hover:text-foreground disabled:opacity-20">
                            <ChevronUp className="h-3 w-3" />
                          </button>
                          <button onClick={() => moveItem(idx, 'down')} disabled={idx === filteredItems.length - 1}
                            className="p-0.5 text-muted-foreground hover:text-foreground disabled:opacity-20">
                            <ChevronDown className="h-3 w-3" />
                          </button>
                        </div>
                      )}

                      {/* Position */}
                      <span className="text-[10px] text-muted-foreground/50 w-5 text-right font-mono">{idx + 1}</span>

                      {/* Flag */}
                      {(activeTab === 'countries' || item.extra?.country_code) && (
                        <span className="text-sm leading-none">
                          {countryFlag(activeTab === 'countries' ? item.id : String(item.extra?.country_code || ''))}
                        </span>
                      )}

                      {/* Name */}
                      <span className="text-sm text-foreground flex-1 truncate">
                        {item.name}
                        {item.inherited === false && (
                          <span className="ml-1.5 text-[9px] bg-blue-500/20 text-blue-400 px-1 py-0.5 rounded font-medium">CUSTOM</span>
                        )}
                      </span>

                      {/* Contextual info */}
                      {activeTab === 'countries' && item.extra?.phone_prefix && (
                        <span className="text-[11px] text-muted-foreground/60 font-mono">{String(item.extra.phone_prefix)}</span>
                      )}
                      {activeTab === 'currencies' && item.extra?.symbol && (
                        <span className="text-[11px] text-muted-foreground/60 font-mono">{String(item.extra.symbol)}</span>
                      )}
                      {activeTab === 'timezones' && item.extra?.utc_offset && (
                        <span className="text-[11px] text-muted-foreground/60 font-mono">UTC{String(item.extra.utc_offset)}</span>
                      )}
                      {activeTab === 'bank_account_formats' && item.extra?.code && (
                        <span className="text-[11px] text-muted-foreground/60 font-mono">{String(item.extra.code)}</span>
                      )}

                      {/* Toggle */}
                      <SwitchToggle checked={!item.disabled} onChange={() => toggleItem(item.id)} size="sm" />
                    </div>
                  ))}
                  {filteredItems.length === 0 && (
                    <div className="p-8 text-center text-xs text-muted-foreground">
                      {catalogSearch ? 'No matches' : 'No items inherited'}
                    </div>
                  )}
                </div>
              </div>

              {/* Add panel */}
              {showAddPanel && (
                <div className="bg-card border border-accent/30 rounded-xl overflow-hidden mt-3">
                  <div className="px-4 py-2.5 border-b border-accent/20 bg-accent/5 flex items-center gap-3">
                    <span className="text-xs font-semibold text-accent uppercase tracking-wider">Add from platform catalog</span>
                    <div className="relative flex-1 max-w-xs">
                      <SearchIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
                      <input value={availableSearch}
                        onChange={e => { setAvailableSearch(e.target.value); loadAvailable(e.target.value) }}
                        placeholder="Search..."
                        className="w-full pl-8 pr-3 py-1.5 rounded-lg border border-border bg-background text-xs focus:outline-none focus:ring-1 focus:ring-ring" />
                    </div>
                  </div>
                  <div className="divide-y divide-border/20 max-h-60 overflow-y-auto">
                    {availableLoading ? (
                      <div className="p-4 text-center text-xs text-muted-foreground">Loading...</div>
                    ) : availableItems.length === 0 ? (
                      <div className="p-4 text-center text-xs text-muted-foreground">No items available to add</div>
                    ) : (
                      availableItems.map((item) => (
                        <div key={item.id} className="flex items-center gap-2 px-4 py-2 hover:bg-muted/10">
                          {(activeTab === 'countries') && (
                            <span className="text-sm leading-none">{countryFlag(item.id)}</span>
                          )}
                          <span className="text-sm text-foreground flex-1 truncate">{item.name}</span>
                          <button onClick={() => addCustomItem(item)}
                            className="text-[11px] font-medium text-accent hover:text-accent/80 px-2 py-1 rounded border border-accent/30 hover:bg-accent/10 transition-colors">
                            + Add
                          </button>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}
              </>
            )}
          </div>
        )}

        {/* ─── PRODUCTS ─── */}
        {activeTab === 'products' && (
          <div className="max-w-xl">
            <SectionCard title="Activated Products" icon={Package}>
              <div className="space-y-3">
                {products.filter(p => !p.is_platform).map((prod) => {
                  const prodId = Number(prod.id)
                  const sp = siteProducts.find(p => p.product_id === prodId)
                  const enabled = sp?.enabled ?? false
                  return (
                    <div key={prodId} className="flex items-center justify-between gap-3">
                      <div className="flex-1">
                        <p className="text-sm font-medium text-foreground">{String(prod.name)}</p>
                        <p className="text-[11px] text-muted-foreground font-mono">{String(prod.code)}</p>
                      </div>
                      {enabled && (
                        <button onClick={() => enterProductConfig(prod)}
                          className="text-[11px] font-medium text-accent hover:text-accent/80 px-2.5 py-1 rounded-lg border border-accent/30 hover:bg-accent/10 transition-colors">
                          Configure
                        </button>
                      )}
                      <SwitchToggle checked={enabled} onChange={() => {
                        setProductConfirm({ prodId, name: String(prod.name), enabling: !enabled })
                      }} />
                    </div>
                  )
                })}
                {products.length === 0 && <p className="text-xs text-muted-foreground">No products in catalog</p>}
              </div>
            </SectionCard>
          </div>
        )}

        {/* ─── GENERAL ─── */}
        {activeTab === 'general' && (
          <div className="space-y-5">
            {/* Status + Site Info */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <SectionCard title="Status" icon={Settings}>
                <div className="space-y-3">
                  <div className="flex gap-1.5">
                    {([
                      { value: 'onboarding', label: 'Onboarding' },
                      { value: 'active', label: 'Live' },
                      { value: 'maintenance', label: 'Maintenance' },
                      { value: 'disabled', label: 'Disabled' },
                    ] as const).map(({ value, label }) => (
                      <button key={value} onClick={async () => {
                        try {
                          await iamApi.put(`/tenants/${site.tenant_id}/sites/${site.id}`, {
                            name: site.name, domain: site.domain, status: value,
                          })
                          setSite({ ...site, status: value })
                          toast.success(`Status changed to ${label}`)
                        } catch { toast.error('Failed to update status') }
                      }} className={`flex-1 px-3 py-2 text-xs font-medium rounded-lg border transition-colors ${
                        site.status === value
                          ? 'bg-accent text-accent-foreground border-accent'
                          : 'bg-background text-muted-foreground border-border hover:border-foreground/30'
                      }`}>{label}</button>
                    ))}
                  </div>
                </div>
              </SectionCard>

              <SectionCard title="Site Info" icon={Globe}>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Name</span>
                    <span className="text-foreground font-medium">{site.name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Domain</span>
                    <span className="text-foreground font-mono text-xs">{site.domain || '—'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Tenant</span>
                    <span className="text-foreground">{site.tenant_id}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">ID</span>
                    <span className="text-foreground font-mono text-xs">{site.id}</span>
                  </div>
                </div>
              </SectionCard>
            </div>

            {/* Jurisdiction summary */}
            <SectionCard title="Jurisdiction" icon={Shield}>
              {jurisdictionInfo?.jurisdiction ? (
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-foreground">{String(jurisdictionInfo.jurisdiction.name)}</p>
                    <p className="text-xs text-muted-foreground">{jurisdictionInfo.country_count || 0} countries inherited</p>
                  </div>
                  <button onClick={() => setActiveTab('jurisdiction')}
                    className="text-[11px] text-accent hover:underline">Edit</button>
                </div>
              ) : (
                <div className="flex items-center justify-between">
                  <p className="text-sm text-muted-foreground">No jurisdiction assigned</p>
                  <button onClick={() => setActiveTab('jurisdiction')}
                    className="text-[11px] text-accent hover:underline">Assign</button>
                </div>
              )}
            </SectionCard>

            {/* Quick stats */}
            {jurisdictionInfo?.jurisdiction_id && (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {CATALOG_KEYS.size > 0 && [...CATALOG_KEYS].slice(0, 4).map(cat => {
                  const label = NAV_SECTIONS.flatMap(s => s.items).find(i => i.key === cat)?.label || cat
                  const Icon = NAV_SECTIONS.flatMap(s => s.items).find(i => i.key === cat)?.icon || Globe
                  return (
                    <button key={cat} onClick={() => setActiveTab(cat)}
                      className="bg-card border border-border rounded-xl p-3 text-center hover:border-accent/30 transition-colors">
                      <Icon className="h-4 w-4 text-accent mx-auto mb-1" />
                      <p className="text-[10px] text-muted-foreground uppercase tracking-wider">{label}</p>
                    </button>
                  )
                })}
              </div>
            )}

            {/* Products summary */}
            <SectionCard title="Products" icon={Package}>
              {(() => {
                const activeProducts = products.filter(p => !p.is_platform && siteProducts.some(sp => sp.product_id === Number(p.id) && sp.enabled))
                return activeProducts.length > 0 ? (
                  <div className="space-y-2">
                    {activeProducts.map(prod => (
                      <div key={String(prod.id)} className="flex items-center justify-between">
                        <div>
                          <span className="text-sm font-medium text-foreground">{String(prod.name)}</span>
                          <span className="text-[11px] text-muted-foreground font-mono ml-2">{String(prod.code)}</span>
                        </div>
                        <button onClick={() => enterProductConfig(prod)}
                          className="text-[11px] text-accent hover:underline">Configure</button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-muted-foreground">No products activated</p>
                    <button onClick={() => setActiveTab('products')}
                      className="text-[11px] text-accent hover:underline">Manage</button>
                  </div>
                )
              })()}
            </SectionCard>

            {/* Theme summary */}
            {siteTheme && (() => {
              const activeTheme = themes.find(t => t.id === siteTheme.theme_id)
              if (!activeTheme) return null
              const themeColors = (() => { try { return JSON.parse(activeTheme.default_config) } catch { return {} } })()
              return (
                <SectionCard title="Theme" icon={Palette}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="flex gap-1">
                        {['primary', 'secondary', 'accent', 'background'].map(c => (
                          <div key={c} className="w-5 h-5 rounded-full border border-border"
                            style={{ background: themeColors?.colors?.[c] || '#ccc' }} />
                        ))}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-foreground">{activeTheme.name}</p>
                        <p className="text-[11px] text-muted-foreground">{activeTheme.slug}</p>
                      </div>
                    </div>
                  </div>
                </SectionCard>
              )
            })()}
          </div>
        )}

        {/* ─── BRANDING ─── */}
        {activeTab === 'p_branding' && (
          <div className="max-w-xl">
            <SectionCard title="Branding Assets" icon={Globe}>
              <div className="space-y-3">
                {[
                  { key: 'logo', label: 'Logo' },
                  { key: 'logoLight', label: 'Logo (Light)' },
                  { key: 'logoDark', label: 'Logo (Dark)' },
                  { key: 'favicon', label: 'Favicon' },
                  { key: 'appleTouchIcon', label: 'Apple Touch Icon' },
                ].map(({ key, label }) => (
                  <Field key={key} label={label} value={(branding[key] as string) || ''}
                    onChange={v => setBranding({ ...branding, [key]: v })} placeholder="https://cdn..." />
                ))}
              </div>
            </SectionCard>
          </div>
        )}

        {/* ─── THEME ─── */}
        {activeTab === 'p_theme' && (() => {
          const colors = (themeConfig.colors || {}) as Record<string, string>
          const typography = (themeConfig.typography || {}) as Record<string, string>
          const spacing = (themeConfig.spacing || {}) as Record<string, string>
          const borders = (themeConfig.borders || {}) as Record<string, string>
          const shadows = (themeConfig.shadows || {}) as Record<string, string>
          const icons = (themeConfig.icons || {}) as Record<string, string>
          const btnPrimary = ((themeConfig.buttons as Record<string, unknown>)?.primary || {}) as Record<string, unknown>
          const btnSignup = ((themeConfig.buttons as Record<string, unknown>)?.signup || {}) as Record<string, unknown>

          const setColor = (key: string, val: string) => setThemeConfig({ ...themeConfig, colors: { ...colors, [key]: val } })
          const setTypo = (key: string, val: string) => setThemeConfig({ ...themeConfig, typography: { ...typography, [key]: val } })
          const setSpacing = (key: string, val: string) => setThemeConfig({ ...themeConfig, spacing: { ...spacing, [key]: val } })
          const setBorders = (key: string, val: string) => setThemeConfig({ ...themeConfig, borders: { ...borders, [key]: val } })
          const setShadows = (key: string, val: string) => setThemeConfig({ ...themeConfig, shadows: { ...shadows, [key]: val } })
          const setIcons = (key: string, val: string) => setThemeConfig({ ...themeConfig, icons: { ...icons, [key]: val } })
          const setBtnPrimary = (key: string, val: unknown) => setThemeConfig({
            ...themeConfig, buttons: { ...(themeConfig.buttons as Record<string, unknown> || {}), primary: { ...btnPrimary, [key]: val } }
          })
          const setBtnSignup = (key: string, val: unknown) => setThemeConfig({
            ...themeConfig, buttons: { ...(themeConfig.buttons as Record<string, unknown> || {}), signup: { ...btnSignup, [key]: val } }
          })

          // Load from preset
          const loadPreset = (th: ThemeResponse) => {
            try { setThemeConfig(JSON.parse(th.default_config)); toast.success(`Loaded ${th.name} preset`) } catch {}
          }

          const themeTabs = [
            { key: 'colors', label: 'Colors' },
            { key: 'buttons', label: 'Buttons' },
            { key: 'typography', label: 'Typography' },
            { key: 'spacing', label: 'Spacing' },
            { key: 'borders', label: 'Borders' },
            { key: 'icons', label: 'Icons' },
          ]

          return (
            <div className="space-y-4">
              {/* Preset + sub-tabs */}
              <div className="flex items-center justify-between">
                <div className="flex gap-1">
                  {themeTabs.map(t => (
                    <button key={t.key} onClick={() => setThemeTab(t.key)}
                      className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                        themeTab === t.key ? 'bg-accent text-accent-foreground' : 'text-muted-foreground hover:text-foreground hover:bg-muted/30'
                      }`}>{t.label}</button>
                  ))}
                </div>
                <div className="flex items-center gap-1.5">
                  <span className="text-[10px] text-muted-foreground">Preset:</span>
                  {themes.map(th => (
                    <button key={th.id} onClick={() => loadPreset(th)}
                      className="flex items-center gap-1 px-2 py-1 text-[10px] rounded border border-border hover:border-accent/50 transition-colors">
                      {(() => { const tc = (() => { try { return JSON.parse(th.default_config) } catch { return {} } })(); return <div className="w-2.5 h-2.5 rounded-full" style={{ background: tc?.colors?.primary || '#ccc' }} /> })()}
                      {th.name}
                    </button>
                  ))}
                </div>
              </div>

              {/* ── Colors tab ── */}
              {themeTab === 'colors' && (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                  <SectionCard title="Brand Colors" icon={Palette}>
                    <div className="space-y-1.5">
                      {[['primary', 'Primary'], ['secondary', 'Secondary'], ['accent', 'Accent']].map(([key, label]) => (
                        <ColorField key={key} label={label} value={colors[key] || ''} onChange={v => setColor(key, v)} />
                      ))}
                    </div>
                  </SectionCard>
                  <SectionCard title="Background Colors" icon={Palette}>
                    <div className="space-y-1.5">
                      {[['background', 'Page'], ['card', 'Card'], ['muted', 'Muted'], ['input', 'Input']].map(([key, label]) => (
                        <ColorField key={key} label={label} value={colors[key] || ''} onChange={v => setColor(key, v)} />
                      ))}
                    </div>
                  </SectionCard>
                  <SectionCard title="Text Colors" icon={Palette}>
                    <div className="space-y-1.5">
                      {[['foreground', 'Page Text'], ['primaryForeground', 'Primary Text'], ['secondaryForeground', 'Secondary Text'],
                        ['accentForeground', 'Accent Text'], ['cardForeground', 'Card Text'], ['mutedForeground', 'Muted Text'],
                      ].map(([key, label]) => (
                        <ColorField key={key} label={label} value={colors[key] || ''} onChange={v => setColor(key, v)} />
                      ))}
                    </div>
                  </SectionCard>
                  <SectionCard title="UI Colors" icon={Palette}>
                    <div className="space-y-1.5">
                      {[['border', 'Border'], ['ring', 'Focus Ring']].map(([key, label]) => (
                        <ColorField key={key} label={label} value={colors[key] || ''} onChange={v => setColor(key, v)} />
                      ))}
                    </div>
                  </SectionCard>
                  <SectionCard title="Status Colors" icon={Palette}>
                    <div className="space-y-1.5">
                      {[['destructive', 'Error'], ['destructiveForeground', 'Error Text'],
                        ['success', 'Success'], ['successForeground', 'Success Text'],
                        ['warning', 'Warning'], ['warningForeground', 'Warning Text'],
                      ].map(([key, label]) => (
                        <ColorField key={key} label={label} value={colors[key] || ''} onChange={v => setColor(key, v)} />
                      ))}
                    </div>
                  </SectionCard>
                </div>
              )}

              {/* ── Buttons tab ── */}
              {themeTab === 'buttons' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-3xl">
                  <SectionCard title="Primary Button" icon={Settings}>
                    <div className="space-y-2">
                      <div className="grid grid-cols-2 gap-2">
                        <ColorField label="BG" value={String(btnPrimary.background || '')} onChange={v => setBtnPrimary('background', v)} />
                        <ColorField label="Hover" value={String(btnPrimary.backgroundAccent || '')} onChange={v => setBtnPrimary('backgroundAccent', v)} />
                      </div>
                      <ColorField label="Text" value={String(btnPrimary.foreground || '')} onChange={v => setBtnPrimary('foreground', v)} />
                      <div className="grid grid-cols-2 gap-2">
                        <Field label="Radius" value={String(btnPrimary.borderRadius || '')} onChange={v => setBtnPrimary('borderRadius', v)} />
                        <Field label="Weight" value={String(btnPrimary.fontWeight || '')} onChange={v => setBtnPrimary('fontWeight', v)} />
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <Field label="Transform" value={String(btnPrimary.textTransform || '')} onChange={v => setBtnPrimary('textTransform', v)} />
                        <Field label="Spacing" value={String(btnPrimary.letterSpacing || '')} onChange={v => setBtnPrimary('letterSpacing', v)} />
                      </div>
                      <div className="flex items-center justify-between pt-1">
                        <span className="text-xs text-muted-foreground">Gradient</span>
                        <SwitchToggle checked={!!btnPrimary.useGradient} onChange={v => setBtnPrimary('useGradient', v)} size="sm" />
                      </div>
                    </div>
                  </SectionCard>
                  <SectionCard title="Signup Button" icon={Settings}>
                    <div className="space-y-2">
                      <div className="grid grid-cols-2 gap-2">
                        <ColorField label="BG" value={String(btnSignup.background || '')} onChange={v => setBtnSignup('background', v)} />
                        <ColorField label="Hover" value={String(btnSignup.backgroundAccent || '')} onChange={v => setBtnSignup('backgroundAccent', v)} />
                      </div>
                      <ColorField label="Text" value={String(btnSignup.foreground || '')} onChange={v => setBtnSignup('foreground', v)} />
                      <div className="grid grid-cols-2 gap-2">
                        <Field label="Radius" value={String(btnSignup.borderRadius || '')} onChange={v => setBtnSignup('borderRadius', v)} />
                        <Field label="Weight" value={String(btnSignup.fontWeight || '')} onChange={v => setBtnSignup('fontWeight', v)} />
                      </div>
                      <div className="flex items-center justify-between pt-1">
                        <span className="text-xs text-muted-foreground">Gradient</span>
                        <SwitchToggle checked={!!btnSignup.useGradient} onChange={v => setBtnSignup('useGradient', v)} size="sm" />
                      </div>
                    </div>
                  </SectionCard>
                </div>
              )}

              {/* ── Typography tab ── */}
              {themeTab === 'typography' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-3xl">
                  <SectionCard title="Font Families" icon={Settings}>
                    <div className="space-y-3">
                      <Field label="Body Font" value={typography.fontFamily || ''} onChange={v => setTypo('fontFamily', v)} placeholder="'Nunito Sans', system-ui, sans-serif" />
                      <Field label="Heading Font" value={typography.fontFamilyHeading || ''} onChange={v => setTypo('fontFamilyHeading', v)} placeholder="Cinzel, Georgia, serif" />
                    </div>
                  </SectionCard>
                  <SectionCard title="Font Sizes" icon={Settings}>
                    <div className="space-y-3">
                      <div className="grid grid-cols-2 gap-2">
                        <Field label="Base" value={typography.fontSize || ''} onChange={v => setTypo('fontSize', v)} placeholder="16px" />
                        <Field label="Small" value={typography.fontSizeSm || ''} onChange={v => setTypo('fontSizeSm', v)} placeholder="14px" />
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <Field label="Large" value={typography.fontSizeLg || ''} onChange={v => setTypo('fontSizeLg', v)} placeholder="18px" />
                        <Field label="XL" value={typography.fontSizeXl || ''} onChange={v => setTypo('fontSizeXl', v)} placeholder="24px" />
                      </div>
                    </div>
                  </SectionCard>
                  <SectionCard title="Line Height & Spacing" icon={Settings}>
                    <div className="space-y-3">
                      <div className="grid grid-cols-2 gap-2">
                        <Field label="Line Height" value={typography.lineHeight || ''} onChange={v => setTypo('lineHeight', v)} placeholder="1.5" />
                        <Field label="Letter Spacing" value={typography.letterSpacing || ''} onChange={v => setTypo('letterSpacing', v)} placeholder="0" />
                      </div>
                    </div>
                  </SectionCard>
                </div>
              )}

              {/* ── Spacing tab ── */}
              {themeTab === 'spacing' && (
                <div className="max-w-md">
                  <SectionCard title="Spacing Scale" icon={Settings}>
                    <div className="space-y-3">
                      <div className="grid grid-cols-3 gap-2">
                        <Field label="XS" value={spacing.xs || ''} onChange={v => setSpacing('xs', v)} placeholder="0.25rem" />
                        <Field label="SM" value={spacing.sm || ''} onChange={v => setSpacing('sm', v)} placeholder="0.5rem" />
                        <Field label="MD" value={spacing.md || ''} onChange={v => setSpacing('md', v)} placeholder="1rem" />
                      </div>
                      <div className="grid grid-cols-3 gap-2">
                        <Field label="LG" value={spacing.lg || ''} onChange={v => setSpacing('lg', v)} placeholder="1.5rem" />
                        <Field label="XL" value={spacing.xl || ''} onChange={v => setSpacing('xl', v)} placeholder="2rem" />
                        <Field label="2XL" value={spacing['2xl'] || ''} onChange={v => setSpacing('2xl', v)} placeholder="3rem" />
                      </div>
                    </div>
                  </SectionCard>
                </div>
              )}

              {/* ── Borders tab ── */}
              {themeTab === 'borders' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-3xl">
                  <SectionCard title="Border Radius" icon={Settings}>
                    <div className="space-y-3">
                      <div className="grid grid-cols-3 gap-2">
                        <Field label="SM" value={borders.radiusSm || ''} onChange={v => setBorders('radiusSm', v)} placeholder="0.25rem" />
                        <Field label="MD" value={borders.radiusMd || ''} onChange={v => setBorders('radiusMd', v)} placeholder="0.375rem" />
                        <Field label="LG" value={borders.radiusLg || ''} onChange={v => setBorders('radiusLg', v)} placeholder="0.5rem" />
                      </div>
                      <Field label="Full" value={borders.radiusFull || ''} onChange={v => setBorders('radiusFull', v)} placeholder="9999px" />
                    </div>
                  </SectionCard>
                  <SectionCard title="Box Shadows" icon={Settings}>
                    <div className="space-y-3">
                      <Field label="Small" value={shadows.sm || ''} onChange={v => setShadows('sm', v)} placeholder="0 1px 2px rgba(0,0,0,0.05)" />
                      <Field label="Medium" value={shadows.md || ''} onChange={v => setShadows('md', v)} placeholder="0 4px 6px rgba(0,0,0,0.1)" />
                      <Field label="Large" value={shadows.lg || ''} onChange={v => setShadows('lg', v)} placeholder="0 10px 15px rgba(0,0,0,0.1)" />
                    </div>
                  </SectionCard>
                </div>
              )}

              {/* ── Icons tab ── */}
              {themeTab === 'icons' && (
                <div className="max-w-md">
                  <SectionCard title="Icons" icon={Settings}>
                    <div className="space-y-3">
                      <Field label="Library" value={icons.library || ''} onChange={v => setIcons('library', v)} placeholder="lucide" />
                      <div className="grid grid-cols-2 gap-2">
                        <Field label="Default Size" value={icons.size || ''} onChange={v => setIcons('size', v)} placeholder="20px" />
                        <Field label="Stroke Width" value={icons.strokeWidth || ''} onChange={v => setIcons('strokeWidth', v)} placeholder="2" />
                      </div>
                    </div>
                  </SectionCard>
                </div>
              )}
            </div>
          )
        })()}

        {/* ─── FEATURES ─── */}
        {activeTab === 'p_features' && (
          <div className="max-w-md">
            <SectionCard title="Feature Flags" icon={ToggleLeft}>
              <div className="space-y-2">
                {[
                  { key: 'tournaments', label: 'Tournaments' },
                  { key: 'loyalty', label: 'Loyalty Program' },
                  { key: 'themeSelector', label: 'Theme Selector' },
                ].map(({ key, label }) => (
                  <div key={key} className="flex items-center justify-between py-1">
                    <span className="text-sm text-foreground">{label}</span>
                    <SwitchToggle checked={!!features[key]} onChange={v => setFeatures({ ...features, [key]: v })} />
                  </div>
                ))}
              </div>
            </SectionCard>
          </div>
        )}

        {/* ─── JSON PREVIEW ─── */}
        {activeTab === 'p_preview' && (
          <div className="bg-card border border-border rounded-xl overflow-hidden">
            <div className="px-5 py-3 border-b border-border bg-muted/20 flex items-center justify-between">
              <div>
                <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">Product Configuration</h3>
                <p className="text-[10px] text-muted-foreground mt-0.5 font-mono">
                  {selectedProduct ? `GET /api/v1/sites/${id}/products/${selectedProduct.id}/config` : ''}
                </p>
              </div>
              <div className="flex gap-1.5">
                <button onClick={() => {
                  if (!selectedProduct) return
                  api.get(`/sites/${id}/products/${selectedProduct.id}/config`).then(({ data }) => setPreviewJSON(JSON.stringify(data, null, 2))).catch(() => {})
                }}
                  className="flex items-center gap-1 px-2.5 py-1 text-[11px] rounded-md border border-border hover:bg-muted transition-colors">
                  <Eye className="h-3 w-3" /> Refresh
                </button>
                <button onClick={() => { if (previewJSON) navigator.clipboard.writeText(previewJSON) }}
                  className="flex items-center gap-1 px-2.5 py-1 text-[11px] rounded-md border border-border hover:bg-muted transition-colors">
                  <Copy className="h-3 w-3" /> Copy
                </button>
              </div>
            </div>
            {previewJSON ? (
              <pre className="text-[11px] p-4 overflow-auto max-h-[calc(100vh-14rem)] font-mono leading-relaxed text-foreground">{previewJSON}</pre>
            ) : (
              <p className="text-sm text-muted-foreground p-8 text-center">Loading...</p>
            )}
          </div>
        )}
      </div>

      {/* ─── PRODUCT CONFIRMATION MODAL ─── */}
      {productConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={() => setProductConfirm(null)}>
          <div className="bg-card border border-border rounded-xl p-6 max-w-sm w-full mx-4 shadow-xl" onClick={e => e.stopPropagation()}>
            <h3 className="text-base font-semibold text-foreground mb-2">
              {productConfirm.enabling ? 'Activate' : 'Deactivate'} product
            </h3>
            <p className="text-sm text-muted-foreground mb-5">
              Are you sure you want to {productConfirm.enabling ? 'activate' : 'deactivate'}{' '}
              <span className="font-medium text-foreground">{productConfirm.name}</span> for this site?
              {!productConfirm.enabling && (
                <span className="block mt-2 text-xs text-red-400">
                  This will disable all features associated with this product.
                </span>
              )}
            </p>
            <div className="flex gap-2 justify-end">
              <button onClick={() => setProductConfirm(null)}
                className="px-4 py-2 text-sm rounded-lg border border-border text-muted-foreground hover:text-foreground transition-colors">
                Cancel
              </button>
              <button onClick={confirmProductToggle}
                className={`px-4 py-2 text-sm rounded-lg font-medium transition-colors ${
                  productConfirm.enabling
                    ? 'bg-accent text-accent-foreground hover:bg-accent/90'
                    : 'bg-red-500 text-white hover:bg-red-600'
                }`}>
                {productConfirm.enabling ? 'Activate' : 'Deactivate'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
